import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ValidationResult {
  isComplete: boolean;
  missingFields: { field: string; label: string }[];
  profileData: any | null;
}

export const useProfileValidation = () => {
  const [isLoading, setIsLoading] = useState(false);

  const validateProfile = useCallback(async (): Promise<ValidationResult> => {
    setIsLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return {
          isComplete: false,
          missingFields: [{ field: 'auth', label: 'User authentication' }],
          profileData: null,
        };
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error || !profile) {
        return {
          isComplete: false,
          missingFields: [{ field: 'profile', label: 'User profile' }],
          profileData: null,
        };
      }

      const missingFields: { field: string; label: string }[] = [];

      // Check required shipping fields
      if (!profile.address_line1 || profile.address_line1.trim() === '') {
        missingFields.push({ field: 'address_line1', label: 'Street address' });
      }
      
      if (!profile.city || profile.city.trim() === '') {
        missingFields.push({ field: 'city', label: 'City' });
      }
      
      if (!profile.state || profile.state.trim() === '') {
        missingFields.push({ field: 'state', label: 'State' });
      }
      
      if (!profile.zip || profile.zip.trim() === '') {
        missingFields.push({ field: 'zip', label: 'ZIP code' });
      }

      // Check for valid phone number (not placeholder)
      if (!profile.phone || profile.phone === '0000000000' || !/^\d{10}$/.test(profile.phone)) {
        missingFields.push({ field: 'phone', label: 'Valid phone number' });
      }

      return {
        isComplete: missingFields.length === 0,
        missingFields,
        profileData: profile,
      };
    } catch (error) {
      console.error('Error validating profile:', error);
      return {
        isComplete: false,
        missingFields: [{ field: 'error', label: 'Error checking profile' }],
        profileData: null,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    validateProfile,
    isLoading,
  };
};
